'use client';

/**
 * CFO-grade toast component. Uses sonner under the hood with custom styling.
 *
 * To use: Replace sonner imports with this module:
 *   import { toast, Toaster } from '@/components/ui-components/Toast';
 *
 * In App.js, use <Toaster /> from this file for top-center placement and CFO styling.
 */

import React from 'react';
import { toast as sonnerToast, Toaster as SonnerToaster } from 'sonner';
import {
  CheckIcon,
  Cross2Icon,
  InfoCircledIcon,
  ExclamationTriangleIcon,
  CrossCircledIcon,
} from '@radix-ui/react-icons';

/** Variant-specific border and icon styling. Uses semantic tokens only. */
const VARIANT_STYLES = {
  info: {
    border: 'border-border',
    iconClass: 'text-text-3',
    Icon: InfoCircledIcon,
  },
  success: {
    border: 'border-success',
    iconClass: 'text-success',
    Icon: CheckIcon,
  },
  warning: {
    border: 'border-warning',
    iconClass: 'text-warning',
    Icon: ExclamationTriangleIcon,
  },
  error: {
    border: 'border-danger',
    iconClass: 'text-danger',
    Icon: CrossCircledIcon,
  },
};

/** Duration by variant (ms). Info/success: 5s, warning: 6s, error: 7s. */
const VARIANT_DURATION = {
  info: 5000,
  success: 5000,
  warning: 6000,
  error: 7000,
};

/**
 * CFO-grade governance notification card.
 * Container: bg-surface, border, rounded-md, shadow-2.
 * Variants differ by border + icon color only (no colored backgrounds).
 */
function ToastCard({ id, title, description, variant = 'info', action, onDismiss }) {
  const styles = VARIANT_STYLES[variant] ?? VARIANT_STYLES.info;
  const { border, iconClass, Icon } = styles;

  const handleDismiss = () => {
    if (onDismiss) onDismiss();
    sonnerToast.dismiss(id);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={[
        'flex items-start gap-3 rounded-md border bg-surface px-4 py-3 text-sm shadow-2 transition duration-150',
        'max-w-sm w-full',
        border,
      ].join(' ')}
    >
      {/* Left icon slot */}
      <span className={`mt-0.5 shrink-0 ${iconClass}`} aria-hidden="true">
        <Icon width={18} height={18} />
      </span>

      {/* Content */}
      <div className="min-w-0 flex-1">
        {title && (
          <p className="text-sm font-semibold text-text">{title}</p>
        )}
        {(description || (!title && description !== undefined)) && (
          <p className={`text-sm text-text-2 ${title ? 'mt-0.5' : ''}`}>
            {description}
          </p>
        )}
        {action && (
          <button
            type="button"
            className="mt-0.5 text-sm text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
            onClick={() => {
              if (action.onClick) action.onClick();
              handleDismiss();
            }}
          >
            {action.label}
          </button>
        )}
      </div>

      {/* Dismiss button (muted CFO style) */}
      <button
        type="button"
        onClick={handleDismiss}
        className="ml-auto shrink-0 rounded-sm p-1 text-text-3 hover:text-text-2 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        aria-label="Dismiss notification"
      >
        <Cross2Icon width={14} height={14} />
      </button>
    </div>
  );
}

/** Normalize title/description from sonner-style args (message or { title, description }). */
function normalizeContent(message, data) {
  if (message == null && data?.description != null) {
    return { title: null, description: data.description };
  }
  if (typeof message === 'object' && message !== null && !React.isValidElement(message)) {
    const { title: t, description: d } = message;
    return { title: t ?? 'Notification', description: d };
  }
  return { title: message ?? 'Notification', description: data?.description };
}

/**
 * CFO-styled toast API. Drop-in for sonner.
 * Use: import { toast } from '@/components/ui-components/Toast'
 */
function showToast(variant, message, data = {}) {
  const { title, description } = normalizeContent(message, data);
  const duration = data.duration ?? VARIANT_DURATION[variant];
  const action = data.action;
  return sonnerToast.custom(
    (id) => (
      <ToastCard
        id={id}
        title={title}
        description={description}
        variant={variant}
        action={action}
      />
    ),
    { duration }
  );
}

const toast = Object.assign(
  (message, data) => showToast('info', message, data),
  {
    success: (msg, data) => showToast('success', msg, data),
    error: (msg, data) => showToast('error', msg, data),
    warning: (msg, data) => showToast('warning', msg, data),
    info: (msg, data) => showToast('info', msg, data),
    message: (msg, data) => showToast('info', msg, data),
    dismiss: (id) => sonnerToast.dismiss(id),
    custom: sonnerToast.custom,
    promise: sonnerToast.promise,
    loading: sonnerToast.loading,
  }
);

/** Position-based placement classes so toasts don't block centered UI (e.g. budget status filter). */
const POSITION_CLASSES = {
  'top-left': '!fixed !top-4 !left-4',
  'top-center': '!fixed !top-4 !left-1/2 !-translate-x-1/2',
  'top-right': '!fixed !top-4 !right-4',
  'bottom-left': '!fixed !bottom-4 !left-4',
  'bottom-center': '!fixed !bottom-4 !left-1/2 !-translate-x-1/2',
  'bottom-right': '!fixed !bottom-4 !right-4',
};

/**
 * CFO-styled Toaster. Place in app root (e.g. App.js).
 * Use position="top-right" or "bottom-right" to avoid blocking centered controls.
 */
function Toaster(props) {
  const position = props.position ?? 'top-center';
  const positionClass = POSITION_CLASSES[position] ?? POSITION_CLASSES['top-center'];
  return (
    <SonnerToaster
      position={position}
      expand={false}
      duration={5000}
      closeButton={false}
      gap={8}
      offset="1rem"
      toastOptions={{
        unstyled: true,
        classNames: {
          toast: 'w-[360px] max-w-[calc(100vw-2rem)]',
        },
      }}
      className={`${positionClass} !z-50 !flex !flex-col !gap-2`}
      {...props}
    />
  );
}

export { toast, Toaster, ToastCard };
export default toast;
